/**
 * @file date-picker/date-picker-dialog.tsx
 *
 * Modal container for Date Pickers — reuses Dialog + DialogPortal primitives.
 * Equivalent to `DatePickerDialog` composable in Android Compose Material3.
 *
 * Tokens from DatePickerModalTokens.kt:
 * - ContainerColor = SurfaceContainerHigh (#ece6f0)
 * - ContainerShape = CornerExtraLarge (28px)
 * - ContainerElevation = Level3
 * - ContainerWidth = 360px, ContainerHeight = 568px (single date)
 *
 * @see https://m3.material.io/components/date-pickers/overview
 */

import * as RadixDialog from "@radix-ui/react-dialog";
import { m } from "motion/react";
import type * as React from "react";
import { cn } from "../../../lib/utils";
import { Dialog, DialogPortal } from "../../dialog";
import {
	PICKER_CONTENT_ANIM,
	PICKER_OVERLAY_ANIM,
} from "../shared/animation-tokens";
import type { DatePickerDialogProps } from "./date-picker.types";

/**
 * Modal Date Picker dialog with MD3 Expressive spring animations.
 *
 * Reuses `<Dialog>` (Root) and `<DialogPortal>` (AnimatePresence + Portal lifecycle)
 * while managing Overlay and Content to preserve MD3 Date Picker layout specs.
 *
 * @example

 * ```tsx
 * const state = useDatePickerState();
 * const [open, setOpen] = React.useState(false);
 *
 * <DatePickerDialog
 *   open={open}
 *   onDismiss={() => setOpen(false)}
 *   confirmButton={
 *     <Button variant="text" onClick={() => { handleConfirm(state.selectedDateMs); setOpen(false); }}>
 *       OK
 *     </Button>
 *   }
 *   dismissButton={
 *     <Button variant="text" onClick={() => setOpen(false)}>Cancel</Button>
 *   }
 * >
 *   <DatePicker state={state} />
 * </DatePickerDialog>
 * ```
 */
export const DatePickerDialog: React.FC<DatePickerDialogProps> = ({
	open,
	onDismiss,
	confirmButton,
	dismissButton,
	children,
	className,
	title = "Select date",
}) => {
	return (
		<Dialog open={open} onOpenChange={(v) => !v && onDismiss()}>
			<DialogPortal open={open}>
				{/* Scrim */}
				<RadixDialog.Overlay asChild forceMount>
					<m.div
						key="picker-overlay"
						className="fixed inset-0 z-50 bg-m3-scrim/40"
						initial={PICKER_OVERLAY_ANIM.initial}
						animate={PICKER_OVERLAY_ANIM.animate}
						exit={PICKER_OVERLAY_ANIM.exit}
						onClick={onDismiss}
					/>
				</RadixDialog.Overlay>

				{/* Dialog panel */}
				<RadixDialog.Content
					asChild
					forceMount
					onEscapeKeyDown={onDismiss}
					onPointerDownOutside={(e) => e.preventDefault()} // scrim handles dismiss
					aria-modal
					aria-describedby={undefined}
				>
					<m.div
						key="picker-content"
						className={cn(
							"fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2",
							"flex flex-col",
							"bg-m3-surface-container-high",
							"rounded-m3-xl",
							"overflow-hidden",
							// Elevation Level 3
							"elevation-3",
							className,
						)}
						style={{ width: "min(calc(100vw - 32px), 360px)" }}
						initial={PICKER_CONTENT_ANIM.initial}
						animate={PICKER_CONTENT_ANIM.animate}
						exit={PICKER_CONTENT_ANIM.exit}
					>
						<RadixDialog.Title className="sr-only">{title}</RadixDialog.Title>

						{/* Content — DatePicker or DateRangePicker */}
						<div className="flex-1 overflow-hidden">{children}</div>

						{/* Action buttons footer */}
						<div className="flex items-center justify-end gap-2 px-4 py-3">
							{dismissButton}
							{confirmButton}
						</div>
					</m.div>
				</RadixDialog.Content>
			</DialogPortal>
		</Dialog>
	);
};

DatePickerDialog.displayName = "DatePickerDialog";
